import React, { useEffect } from 'react'
import { Head, useForm, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import { KeyRound, Lock, ShieldCheck } from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'

// 🚨 Field MUST live at module scope, NOT inside ChangePassword (Paul 2026-08-13).
// Declaring it in the component body makes it a BRAND-NEW component type on every render, so React
// unmounted and remounted the <input> after each keystroke — the caret jumped out of the box and you
// had to click back in to type the next character. Hoisted = one stable type = focus is kept.
function Field({ label, name, value, autoComplete, error, onChange }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-brand-ink">{label}</label>
      <div className="relative">
        <Lock size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
        <input
          type="password"
          name={name}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          className="w-full rounded-xl border border-brand-border bg-white py-2 pl-9 pr-3 text-sm text-brand-ink outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
        />
      </div>
      {error && <p className="mt-1 text-xs font-medium text-red-600">{error}</p>}
    </div>
  )
}

// Self-service change-password page — any logged-in staff member (not admin-only).
// PATCH /account/password: the server verifies the CURRENT password (bcrypt) before setting the new one.
export default function ChangePassword() {
  const page = usePage()
  const flash = page.props?.flash || {}
  const { data, setData, patch, processing, errors: formErrors, reset } = useForm({
    current_password: '',
    password: '',
    password_confirmation: '',
  })
  // The controller redirects on validation failure with `inertia: { errors: … }`, so the messages
  // arrive in the SHARED page-props error bag after the redirect — merge both so they always render.
  const errors = { ...(page.props?.errors || {}), ...formErrors }

  // Toast the server's success flash after a successful PATCH round-trip.
  useEffect(() => {
    if (flash.notice) {
      toast.success(flash.notice)
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flash.notice])

  const submit = (e) => {
    e.preventDefault()
    patch('/account/password', { preserveScroll: true })
  }

  return (
    <DashboardLayout>
      <Head title="Change Password" />
      <div className="mx-auto max-w-md p-4 md:p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary/10 text-brand-primary">
            <KeyRound size={20} />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-brand-ink">Change Password</h1>
            <p className="text-sm text-brand-muted">Update the password for your own login.</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
          <Field label="Current password" name="current_password" value={data.current_password} autoComplete="current-password" error={errors.current_password} onChange={setData} />
          <Field label="New password" name="password" value={data.password} autoComplete="new-password" error={errors.password} onChange={setData} />
          <Field label="Confirm new password" name="password_confirmation" value={data.password_confirmation} autoComplete="new-password" error={errors.password_confirmation} onChange={setData} />

          <div className="flex items-center gap-2 rounded-lg bg-brand-surface/60 px-3 py-2 text-xs text-brand-muted">
            <ShieldCheck size={14} className="flex-shrink-0 text-brand-primary" />
            Use at least 8 characters. You will stay signed in after changing it.
          </div>

          <button
            type="submit"
            disabled={processing}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-primary-dark disabled:opacity-60"
          >
            <KeyRound size={15} /> {processing ? 'Saving…' : 'Update password'}
          </button>
        </form>
      </div>
    </DashboardLayout>
  )
}
